// Layer 3 (Transformations): the GM landing's lead band (komatsu.K12e #647,
// locked comp `gm-landing.html` B). Three strip-as-tabs cells — month EBITDA
// vs Budget · escalations · FY outlook — beside one exec KPI, the open-case
// exposure (D20), and each tab's hairline figure row. Every figure reads an
// existing K12 function; nothing here computes money of its own.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { FmeaLearningSessionState } from '@/versions/v1/shared/FmeaLearningEngine';
import type { VizTone } from '@/versions/v1/modules/_viz';
import type { PipelineActor } from '@/versions/v1/modules/advisory/PipelineStrip.transform';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import {
  exposureHeadline,
  openCaseExposurePerDay,
  pricedDriverTree,
} from '@/versions/v1/modules/advisory/DriverTree/Scene.transform';
import { formatCurrencyStandard } from '@/versions/v1/shared/money';
import { gmAmount, gmBudgetTone, gmMoney } from './GmMoney.transform';
import { escalationCounts, escalationShortName } from './GmTabs.transform';
import { managerExecKpis, managerPerfKpis } from './RoleLandingTabKpis.transform';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';

export type GmTabId = 'exec' | 'esc' | 'perf';

/** One strip-as-tabs cell (the Advisory `PipelineStrip` tabs-mode recipe). */
export type LandingStripCell<Id extends string = string> = {
  id: Id;
  name: string;
  figure: string;
  figureContext?: string;
  /** Optional 7px state dot before the figure when the cell counts an abnormal
   *  condition (the Operator's areas off band, polish.P16). Default none. */
  figureFlag?: 'warn' | 'bad';
  comparisonLead: string;
  /** Optional state tone on the lead term itself, where the lead is the
   *  comparison that sets the tone (`vs budget 720 t · +130 t`, polish.P15).
   *  Default none: the lead reads ink-secondary. */
  comparisonLeadTone?: 'ok' | 'warn' | 'bad';
  comparisonRest: string;
  /** Optional state-toned tail after the comparison (`US$35K over`). Default none. */
  comparisonFlag?: { text: string; tone: 'ok' | 'warn' | 'bad' };
  actor: PipelineActor;
  actorLabel?: string;
  /** Optional further actor chips after the first (`Agent` then `Human · Control met`). Default none. */
  moreActors?: readonly { actor: PipelineActor; label?: string }[];
  /** Optional dashed provenance chip after the actors (`derived`), its source in the tooltip. Default none. */
  provenance?: { text: string; title: string };
};

export type GmStripCell = {
  id: GmTabId;
  name: string;
  figure: string;
  figureContext?: string;
  /** Comparison: the lead term carries the weight, the rest is context (DESIGN § The primary figure). */
  comparisonLead: string;
  comparisonRest: string;
  actor: PipelineActor;
  actorLabel?: string;
};

export function gmStripCells(
  state: SessionState,
  recorded: ReadonlySet<string> = new Set(),
  fmeaLearning?: FmeaLearningSessionState,
): GmStripCell[] {
  const exec = managerExecKpis(state);
  const ebitda = exec.find((tile) => tile.id === 'mgr-exec-1')!;
  const g = gmMoney();
  const { decisions, patterns, today, todayRecorded } = escalationCounts(state, recorded, fmeaLearning);
  const central = g.forecast.scenarios.find((s) => s.name === 'Central')!;
  const todayLead = today[0];
  const todayDone = todayRecorded[0];
  return [
    {
      id: 'exec',
      name: ebitda.label,
      figure: ebitda.value,
      comparisonLead: `vs Budget ${gmAmount(g.mtd.ebitda.target)}`,
      // One comparator, as the comp carries; `/cost` keeps the design potential.
      comparisonRest: '',
      actor: 'Engine',
    },
    {
      id: 'esc',
      name: 'Escalations',
      figure: String(decisions),
      figureContext: decisions === 1 ? 'decision' : 'decisions',
      comparisonLead: `${today.length} due today`,
      comparisonRest: `${todayLead ? ` · ${escalationShortName(todayLead)}` : todayDone ? ` · ${escalationShortName(todayDone)} recorded` : ''}${patterns > 0 ? ` · +${patterns} pattern${patterns === 1 ? '' : 's'}` : ''}`,
      actor: 'Human',
      actorLabel: 'GM',
    },
    {
      id: 'perf',
      name: 'FY outlook',
      figure: gmAmount(central.ebitda),
      figureContext: 'EBITDA · central',
      comparisonLead: `vs Budget ${gmAmount(g.forecast.budget.ebitda)}`,
      comparisonRest: ` · ${g.forecast.scenarios.length} Planning cases`,
      actor: 'Engine',
    },
  ];
}

export type GmAtStakeKpi = {
  label: string;
  /** The open-case exposure (D20), in Advisory's whole-dollar spelling. */
  figure: string;
  unit: string;
  comparisonLead: string;
  comparisonRest: string;
};

/** D20: the exec KPI reads `openCaseExposurePerDay`. The figure prints through
 * `formatCurrencyStandard`, the formatter the Advisory Cases cell it opens uses, so
 * both read US$4,664/day; the split keeps `exposureHeadline`'s cents. */
export function gmAtStakeKpi(state: SessionState): GmAtStakeKpi {
  const perDay = openCaseExposurePerDay(pricedDriverTree(DRIVER_TREE_SEED, state), state.cursor);
  const exposure = exposureHeadline(perDay);
  return {
    label: 'Active cases',
    figure: formatCurrencyStandard(Math.max(0, perDay.total)),
    unit: '/day at stake',
    comparisonLead: 'vs design',
    comparisonRest: ` · ${exposure.split}`,
  };
}

export type GmMiniItem = {
  id: string;
  label: string;
  value: string;
  caption: string;
  tone: VizTone;
  /** Optional lead term set in 600 ink-secondary before the caption (comp `.cmp b`). Default none. */
  captionLead?: string;
  /** Optional unit set on the value's baseline in 600 muted (`t milled`, `/ 12 h`). Default none. */
  unit?: string;
  /** Optional signed delta in its state tone after the lead, set off by ` · ` (`+130 t`). Default none. */
  captionDelta?: { text: string; tone: 'ok' | 'warn' | 'bad' };
  /** Optional 4 px progress rule under the value: the elapsed share filled,
   *  one info tick at a marked point, and a right-hand caption (Ops supervisor
   *  shift hours, P13). Default none. */
  rule?: { filledPct: number; tickPct: number; tickTitle: string; aside: string };
  /** Optional hand-off set at the right of the label line (`Downtime ↗`), for a
   *  figure another route owns (Maintenance landing, P14). Default none. */
  link?: { to: string; label: string };
};

function znRecoveryItem(state: SessionState): GmMiniItem {
  const reading = readingsAt(state).find((r) => r.id === 'rd-kpi-zn-rec') ?? readingByIdLookup('rd-kpi-zn-rec');
  const actual = reading?.value ?? 0;
  const budget = reading?.target ?? 0;
  const design = reading?.design ?? 0;
  return {
    id: 'gm-zn-rec',
    label: 'Zn recovery',
    value: `${actual.toFixed(1)}%`,
    caption: `vs ${budget.toFixed(1)}% budget · ${design.toFixed(1)}% design`,
    tone: gmBudgetTone(actual, budget),
  };
}

/** Executive summary figure row: production, Zn recovery, prices. The month
 * EBITDA and the exposure moved up into the strip and the exec KPI. */
export function gmExecMiniRow(state: SessionState): GmMiniItem[] {
  const exec = managerExecKpis(state);
  const tile = (id: string): GmMiniItem => {
    const row = exec.find((t) => t.id === id)!;
    return { id: row.id, label: row.label, value: row.value, caption: row.caption, tone: row.tone };
  };
  return [tile('mgr-exec-2'), znRecoveryItem(state), tile('mgr-exec-4')];
}

/** Performance figure row: tonnes to date, full-year tonnes and NSR. The
 * full-year EBITDA sits in the FY outlook cell and the scenario table. */
export function gmPerfMiniRow(): GmMiniItem[] {
  return managerPerfKpis()
    .filter((row) => row.id !== 'mgr-perf-4')
    .map((row) => ({ id: row.id, label: row.label, value: row.value, caption: row.caption, tone: row.tone }));
}
